import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useSite } from "../lib/site";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const chip = (on) => `rounded-md border px-3 py-1.5 text-xs transition-colors ${on ? "border-rose-500 bg-rose-500/20 text-rose-100" : "border-slate-700 text-slate-400 hover:text-slate-100"}`;
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export const MachineAssign = ({ user, onClose, onSaved }) => {
  const { site } = useSite();
  const [machines, setMachines] = useState([]);
  const [secs, setSecs] = useState([]);
  const [ids, setIds] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!user) return;
    setSecs(user.machine_sections || []);
    setIds(user.machine_ids || []);
    api.get("/assets", { params: { kind: "machine" } }).then((r) => setMachines(r.data));
  }, [user]);
  if (!user) return null;
  const sections = [...new Set(machines.map((m) => m.section).filter(Boolean))].sort();
  const shown = machines.filter((m) => !q || `${m.asset_code} ${m.name} ${m.section}`.toLowerCase().includes(q.toLowerCase()));
  const covered = machines.filter((m) => secs.includes(m.section) || ids.includes(m.id)).length;
  const save = async () => {
    try {
      await api.patch(`/users/${user.id}`, { machine_sections: secs, machine_ids: ids });
      toast.success(`Machine checks assigned to ${user.name}`);
      onSaved();
      onClose();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-2xl" data-testid="machine-assign-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Machine checks - {user.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">{site?.name}: assign whole sections (includes machines added later) and/or individual machines. <span className="font-semibold text-slate-200" data-testid="machine-assign-count">{covered} machine(s) assigned</span></p>
        <div>
          <p className="eyebrow mb-2">Sections</p>
          <div className="flex flex-wrap gap-2">
            {sections.map((s) => <button key={s} type="button" onClick={() => setSecs(toggle(secs, s))} className={chip(secs.includes(s))} data-testid={`assign-section-${s}`}>{s} ({machines.filter((m) => m.section === s).length})</button>)}
            {!sections.length && <p className="text-xs text-slate-500">No machine sections on this site.</p>}
          </div>
        </div>
        <div>
          <p className="eyebrow mb-2">Individual machines</p>
          <input className="field mb-2 h-10" placeholder="Search machines..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="assign-machine-search" />
          <div className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto">
            {shown.map((m) => {
              const viaSection = secs.includes(m.section);
              return (
                <button key={m.id} type="button" disabled={viaSection} onClick={() => setIds(toggle(ids, m.id))} className={`${chip(viaSection || ids.includes(m.id))} disabled:opacity-60`} data-testid={`assign-machine-${m.asset_code}`}>
                  {m.asset_code}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="machine-assign-cancel">Cancel</Button>
          <Button className="bg-rose-600 hover:bg-rose-700" onClick={save} data-testid="machine-assign-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
